var app = angular.module('leonardo');

/**
 * WorkOrderExpoDialog - Angular controller for the Expo work order dialog.
 * This controller is responsible for:
 * 1. Loading the Expo app in an iframe
 * 2. Passing initialization data to the Expo app
 * 3. Handling messages from the Expo app
 * 4. Dialog management (close, confirm, etc.)
 * 
 * Note: All API calls related to work order data are now handled directly
 * by the Expo app for simplicity and better architectural separation.
 */
var WorkOrderExpoDialog = function (
  $scope,
  $q,
  $sce,
  workOrderObj,
  FilterService,
  PropertyService,
  AuthDataService,
  AuthService,
  UnitService,
  $timeout,
  ngDialog,
  WorkOrderService,
  StatsigService
) {
  // Construct URL to Expo app
  var expoAppBase = '/react-native-web/index.html';
  $scope.expoAppUrl = $sce.trustAsResourceUrl(expoAppBase);
  
  // UI state
  $scope.loading = true;
  $scope.errorMessage = null;
  $scope.canSubmit = false;

  // Guard flag to ensure logout is only triggered once per dialog session
  var sessionExpiredHandled = false;

  /**
   * Handle messages from the Expo app
   */
  function handleMessage(event) {
    // Verify message origin for security
    if (event.origin !== window.location.origin) {
      console.warn('[WorkOrderExpoDialog] Received message from unexpected origin:', event.origin);
      return;
    }
    
    var message = event.data;

    if (!message || !message.type) {
      return;
    }
    
    $scope.$apply(function() {
      switch(message.type) {
        case 'EXPO_INITIALIZED':
          handleExpoInitialized();
          break;
          
        case 'EXPO_WORK_ORDER_SUBMIT':
        case 'SUBMIT':  // Support alternate message type from Expo
          handleWorkOrderSubmit(message.payload);
          break;
          
        case 'EXPO_WORK_ORDER_CLOSE':
        case 'CLOSE':  // Support alternate message type from Expo
          $scope.closeThisDialog();
          break;
          
        case 'EXPO_ERROR':
          showError(message.payload && message.payload.message ? message.payload.message : 'Unknown error');
          break;

        case 'EXPO_SESSION_EXPIRED':
          handleSessionExpired();
          break;
          
        default:
          break;
      }
    });
  }

  /**
   * Handle session expiration reported by the Expo app.
   * Triggers a single logout so the user is redirected to the login page.
   */
  function handleSessionExpired() {
    if (sessionExpiredHandled) {
      return;
    }
    sessionExpiredHandled = true;

    console.warn('[WorkOrderExpoDialog] EXPO_SESSION_EXPIRED received — logging out');

    // Pass true to skip the backend logout call; the token is already invalid.
    AuthService.logout(true);
  }
  
  /**
   * Handle Expo initialization
   * 
   * This function is called when the Expo app signals it's initialized.
   * Sends initialization data to the Expo app including:
   * - Work order data (if editing)
   * - Property and unit information
   * - JWT token (for direct API calls from Expo)
   */
  async function handleExpoInitialized() {
    // Check if we're in an iframe
    var inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch (e) {
      inIframe = true;
    }

    // Check the feature gate
    var gateEnabled = await StatsigService.checkGate('work_orders_new_experience');

    // If in iframe and gate is disabled, show feature gate dialog over the modal
    if (inIframe && !gateEnabled) {
      ngDialog.open({
        template: 'views/templates/dialog/feature-gate.html',
        className: 'ngdialog-theme-default ngdialog-feature-gate',
        controller: 'FeatureGateDialog',
        data: {
          parentScope: $scope
        }
      });
      // Continue initialization to show the modal in background
    }

    var action = $scope.ngDialogData.action || 'create';
    var workOrderId = $scope.ngDialogData.workOrderId || null;
    var task = $scope.ngDialogData.task || null;
    var property = $scope.ngDialogData.property || FilterService.currentProperty || null;
    var unit = $scope.ngDialogData.unit || null;
    var account = $scope.ngDialogData.account || (property && property.account ? property.account : null);
    
    // Extract work order data if available (for edit mode)
    var workOrderData = $scope.ngDialogData.work_order || workOrderObj || null;
    
    // Handle cases where property/account/unit might be IDs or objects
    var propertyId = null;
    var accountId = null;
    var unitId = null;
    
    if (property) {
      propertyId = typeof property === 'object' ? property.id : property;
    } else if (workOrderData && workOrderData.property) {
      propertyId = typeof workOrderData.property === 'object' ? workOrderData.property.id : workOrderData.property;
      if (typeof workOrderData.property === 'object') {
        property = workOrderData.property;  // Use the full object if available
      }
    }
    
    if (account) {
      accountId = typeof account === 'object' ? account.id : account;
    } else if (workOrderData && workOrderData.account) {
      accountId = typeof workOrderData.account === 'object' ? workOrderData.account.id : workOrderData.account;
      if (typeof workOrderData.account === 'object') {
        account = workOrderData.account;  // Use the full object if available
      }
    }
    
    if (unit) {
      unitId = typeof unit === 'object' ? unit.id : unit;
    } else if (workOrderData && workOrderData.unit_id) {
      unitId = workOrderData.unit_id;
    }
    
    // Optional work order fields
    var requesterName = $scope.ngDialogData.requesterName || (workOrderData ? workOrderData.requester_name : null) || null;
    var requesterEmail = $scope.ngDialogData.requesterEmail || (workOrderData ? workOrderData.requester_email : null) || null;
    var hasPermissionToEnter = $scope.ngDialogData.hasPermissionToEnter || (workOrderData ? workOrderData.has_permission_to_enter : null) || null;
    var tenantCaused = $scope.ngDialogData.tenantCaused || (workOrderData ? workOrderData.tenant_caused : null) || null;

    var dataToSend = {
      // Action and IDs
      action: action,
      workOrderId: workOrderId,
      viewMode: action === 'edit' ? 'preview' : undefined,
      
      // Flat IDs (required by Expo app)
      propertyId: propertyId,
      accountId: accountId,
      unitId: unitId,
      
      // Optional work order fields
      requesterName: requesterName,
      requesterEmail: requesterEmail,
      hasPermissionToEnter: hasPermissionToEnter,
      tenantCaused: tenantCaused,
      
      // API connectivity
      baseUrl: window.LEO_BASE_URL,
      jwtToken: localStorage.getItem('jwt_token'),
      
      // User information
      userInfo: {
        id: AuthDataService.authData.user.id,
        email: AuthDataService.authData.user.email,
        first_name: AuthDataService.authData.user.first_name,
        last_name: AuthDataService.authData.user.last_name,
        name: AuthDataService.authData.user.first_name + ' ' + AuthDataService.authData.user.last_name,
        full_name: AuthDataService.authData.user.first_name + ' ' + AuthDataService.authData.user.last_name
      },
      
      // Property information (full object for additional context)
      property: property ? {
        id: property.id,
        name: property.name,
        pms_platform: property.pms_platform,
        pms_external_id: property.pms_external_id
      } : null,
      
      // Unit information (if applicable)
      unit: unit ? {
        id: unit.id || unit,
        number: unit.number,
        building: unit.building
      } : null,
      
      // Task information (if creating from task dialog)
      task: task ? {
        id: task.id,
        name: task.name,
        property: task.property,
        unit_id: task.unit_id
      } : null,
      
      // Account information
      account: account ? {
        id: account.id || account,
        name: account.name
      } : null
    };
    
    // Send INIT message with work-order mode
    sendMessageToExpo('INIT', JSON.parse(JSON.stringify(dataToSend)), 'work-order');

    $scope.loading = false;
    $scope.canSubmit = true;
  }
  
  /**
   * Handle work order submission from Expo
   * 
   * This function receives data from the Expo app and handles dialog actions.
   * The Expo app is now responsible for all API calls to save work order data.
   * This controller only needs to handle dialog control (close/confirm) actions.
   */
  function handleWorkOrderSubmit(payload) {
    console.log('[WorkOrderExpoDialog] Received SUBMIT payload:', payload);
    
    var workOrderId = payload.workOrderId;
    var action = payload.action;
    
    // For create operations, workOrderId might be the newly created ID
    // For edit operations, it should match the existing ID
    // Accept the submission and let parent handle the result
    
    // Confirm with the work order data so parent can refresh
    $scope.confirm({ 
      workOrderId: workOrderId,
      action: action,
      success: true
    });
  }
  
  /**
   * Send a message to the Expo app iframe
   * 
   * @param {string} type - Message type identifier
   * @param {object} payload - Message payload data
   * @param {string} mode - Optional mode parameter (e.g., 'work-order', 'dynamic-form')
   */
  function sendMessageToExpo(type, payload, mode) {
    var iframe = document.getElementById('expo-app-iframe');
    if (!iframe) {
      console.warn('[WorkOrderExpoDialog] Iframe not found');
      return;
    }
    
    var message = {
      type: type,
      payload: payload || {}
    };
    
    // Add mode parameter for INIT messages
    if (mode) {
      message.mode = mode;
    }
    
    console.log('[WorkOrderExpoDialog] Sending message:', message.type, mode ? '(mode: ' + mode + ')' : '');
    iframe.contentWindow.postMessage(message, '*');
  }
  
  /**
   * Show error message to the user
   * 
   * @param {string} message - Error message to display
   */
  function showError(message) {
    $scope.loading = false;
    $scope.errorMessage = message;
    
    // Show error dialog
    $timeout(function() {
      ngDialog.open({
        template: 'views/templates/dialog/error-message.html',
        className: 'ngdialog-theme-default',
        data: { message: message }
      });
    }, 500);
  }
  
  /**
   * Initialize the controller
   */
  function init() {
    window.addEventListener('message', handleMessage);

    $scope.$on('ngDialog.opened', function (e, $dialog) {
      $scope.$apply(function() {
        $scope.loading = false;
      });
    });
  }
  
  $scope.$on('$destroy', function () {
    window.removeEventListener('message', handleMessage);
  });
  
  init();
};

app.controller('WorkOrderExpoDialog', [
  '$scope',
  '$q',
  '$sce',
  'workOrderObj',
  'FilterService',
  'PropertyService',
  'AuthDataService',
  'AuthService',
  'UnitService',
  '$timeout',
  'ngDialog',
  'WorkOrderService',
  'StatsigService',
  WorkOrderExpoDialog
]);